/**
 * PlayerState - ES5-compatible reactive state management for video player
 * Inspired by Vidstack's signal-based state system
 */

/**
 * Create initial player state
 */
function createInitialState() {
    return {
        // Playback state
        paused: true,
        playing: false,
        waiting: false,
        seeking: false,
        ended: false,

        // Time
        currentTime: 0,
        duration: 0,
        buffered: 0,
        bufferedEnd: 0,

        // Volume
        volume: 1,
        muted: false,

        // Ready state
        canPlay: false,
        isReady: false,

        // Loading
        loading: true,
        error: null,

        // Tracks
        qualities: [],
        currentQuality: -1,  // -1 = auto
        audioTracks: [],
        currentAudioTrack: 0,
        textTracks: [],
        currentTextTrack: -1,  // -1 = off

        // Source
        source: null,
        sourceType: null,

        // Fullscreen
        fullscreen: false,

        // Controls
        controlsVisible: true,

        // Stream info
        live: false,
        streamType: 'on-demand',
    };
}

/**
 * PlayerState class - manages reactive state with subscribers
 */
function PlayerState() {
    var self = this;
    self._state = createInitialState();
    self._subscribers = [];
    self._propertySubscribers = {};
}

/**
 * Get current state
 */
PlayerState.prototype.getState = function () {
    return this._state;
};

/**
 * Get a specific property value
 */
PlayerState.prototype.get = function (key) {
    return this._state[key];
};

/**
 * Set a property and notify subscribers
 */
PlayerState.prototype.set = function (key, value) {
    var oldValue = this._state[key];
    if (oldValue === value) return;

    this._state[key] = value;

    // Notify property-specific subscribers
    if (this._propertySubscribers[key]) {
        for (var i = 0; i < this._propertySubscribers[key].length; i++) {
            try {
                this._propertySubscribers[key][i](value, oldValue, key);
            } catch (e) {
                console.error('[PlayerState] Subscriber error:', e);
            }
        }
    }

    // Notify global subscribers
    for (var j = 0; j < this._subscribers.length; j++) {
        try {
            this._subscribers[j](key, value, oldValue);
        } catch (e) {
            console.error('[PlayerState] Global subscriber error:', e);
        }
    }
};

/**
 * Set multiple properties at once
 */
PlayerState.prototype.batch = function (updates) {
    var keys = Object.keys(updates);
    for (var i = 0; i < keys.length; i++) {
        this.set(keys[i], updates[keys[i]]);
    }
};

/**
 * Subscribe to all state changes
 */
PlayerState.prototype.subscribe = function (callback) {
    var self = this;
    self._subscribers.push(callback);

    // Return unsubscribe function
    return function () {
        var index = self._subscribers.indexOf(callback);
        if (index > -1) {
            self._subscribers.splice(index, 1);
        }
    };
};

/**
 * Subscribe to a specific property
 */
PlayerState.prototype.on = function (property, callback) {
    var self = this;
    if (!self._propertySubscribers[property]) {
        self._propertySubscribers[property] = [];
    }
    self._propertySubscribers[property].push(callback);

    // Return unsubscribe function
    return function () {
        var arr = self._propertySubscribers[property];
        var index = arr.indexOf(callback);
        if (index > -1) {
            arr.splice(index, 1);
        }
    };
};

/**
 * Reset state to initial values
 */
PlayerState.prototype.reset = function () {
    var initial = createInitialState();
    var keys = Object.keys(initial);
    for (var i = 0; i < keys.length; i++) {
        this.set(keys[i], initial[keys[i]]);
    }
};

// Export for ES modules and CommonJS
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { PlayerState: PlayerState, createInitialState: createInitialState };
}

export { PlayerState, createInitialState };
